import React, {FormEvent, useState} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../components/content-box/ContentBox";
import PageActions from "../../../base/PageActions";
import Color from "../../../../model/Color";
import ColorPreview from "../../../../components/color-preview/ColorPreview";
import FursonaGenQuery from "../../../../model/FursonaGenQuery";
import {
    FursonaGeneratorModifiers,
    GeneratorCategory,
    GeneratorColorSet
} from "../../../../model/generator/GeneratorTypes";
import _http_service from "../../../../services/HttpService";
import _image_service from "../../../../services/ImageService";
import '../../../styles/Common.scss';
import '../../../styles/Form.scss';
import './FursonaGeneratorPage.scss';

interface FursonaGeneratorPageProps {
}

interface FursonaGeneratorPageState {
    creatureName?: string,
    creatureQualifier?: string,
    colors?: Color[],
    modifier?: string
}

const FursonaGeneratorPage = (props: FursonaGeneratorPageProps) => {

    const _base_state: FursonaGenQuery = {
        category: 'ALL',
        colors: 0,
        colorSet: 'ALL',
        modifier: 'NONE'
    };

    const page: PageActions = new PageActions({
        title: `Random Fursona Generator - Chase the Fox`,
        description: `Looking for inspiration? Generate a random fursona for yourself or as a prompt for doodling!`,
        keywords: ["fursona generator", "fursona randomizer", "furry generator", "furry randomizer"],
        imgSrc: `chase/assets/chase_artist.png`,
        url: `fursona-generator`
    });

    const [state, setState] = useState<FursonaGeneratorPageState | undefined>(undefined);
    const [query, setQuery] = useState<FursonaGenQuery>({..._base_state});

    const submitForm = (): void => {
        _http_service.getFursonaGeneratorResult(query)
            .then(response => {
                setState({
                    creatureName: (response.subName) ? response.subName : response.name,
                    creatureQualifier: response.mythical ? 'Mythical' : '',
                    colors: response.colors,
                    modifier: response.modifier
                });
            }).catch(ignored => setState({}));
    }

    const formatColors = (): JSX.Element => {
        if (state === undefined || state.colors === undefined || state.colors.length <= 0) {
            return <></>;
        }

        if (!state.colors || state.colors.length <= 0) {
            return <></>;
        } else if (state.colors.length === 1) {
            return <span><ColorPreview color={state.colors[0]}/></span>;
        } else if (state.colors.length === 2) {
            return <span><ColorPreview color={state.colors[0]}/> and <ColorPreview color={state.colors[1]}/></span>;
        }

        return <span><ColorPreview color={state.colors[0]}/>, <ColorPreview color={state.colors[1]}/>,
            and <ColorPreview color={state.colors[2]}/></span>;
    }

    const getFullCreatureName = (): string => {
        if (!state || !state?.creatureName) {
            return '';
        }

        return (state.creatureQualifier)
            ? `${state.creatureQualifier} ${state.creatureName}`
            : state.creatureName;
    }

    return page.generateMetaContent(
        <>
            <ContentBox header="Fursona Generator (Beta)">
                <div className="fur-gen-layout">
                    <div className="fur-gen-controls">
                        <div className="fur-gen-intro">
                            <p>
                                I've set up this tool to provide inspiration to those who seek it. Are you looking for an
                                idea for a new original character or fursona? Are you looking for a fun drawing prompt?
                                Are you seeking something out of the ordinary to go crazy with? Give this generator a shot
                                then!
                            </p>
                        </div>
                        <form className="form-body">
                            <div>
                                <div className="fur-form-col-labels">
                                    <label htmlFor="category">Category:</label>
                                </div>
                                <div className="fur-form-col-content">
                                    <select id="category-select"
                                            className="form-select"
                                            name="category"
                                            value={query.category}
                                            onChange={(event) => {
                                                let category = event.target.value;
                                                setQuery({...query, category});
                                            }}
                                    >
                                        {GeneratorCategory.map(key =>
                                            <option key={`fur-gen-category-${key.toLowerCase()}`}
                                                    value={key.substring(0, key.endsWith('s') ? key.length - 1 : key.length).toUpperCase()}>
                                                {key}
                                            </option>
                                        )}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <div className="fur-form-col-labels">
                                    <label htmlFor="category">Modifier:</label>
                                </div>
                                <div className="fur-form-col-content">
                                    <select id="modifier-select"
                                            className="form-select"
                                            name="modifier"
                                            value={query.modifier}
                                            onChange={(event) => {
                                                let modifier = event.target.value;
                                                setQuery({...query, modifier});
                                            }}
                                    >
                                        {Object.entries(FursonaGeneratorModifiers).map(entry =>
                                            <option key={`fur-gen-modifier-${entry[0]}`}
                                                    value={entry[0]}>
                                                {entry[1]}
                                            </option>
                                        )}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <div className="fur-form-col-labels">
                                    <label htmlFor="color-count">Number of Colors:</label>
                                </div>
                                <div className="fur-form-col-content">
                                    <select id="color-count-select"
                                            className="form-select"
                                            name="color-count"
                                            value={query.colors}
                                            onChange={(event) => {
                                                let colors = +event.target.value;
                                                setQuery({...query, colors});
                                            }}
                                    >
                                        {[0, 1, 2, 3].map(num => <option key={`color-${num}`} value={num}>{num}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <div className="fur-form-col-labels">
                                    <label htmlFor="category">Color Set:</label>
                                </div>
                                <div className="fur-form-col-content">
                                    <select id="color-set-select"
                                            className="form-select"
                                            name="color-set"
                                            value={query.colorSet}
                                            disabled={query.colors <= 0}
                                            onChange={(event) => {
                                                let colorSet = event.target.value;
                                                setQuery({...query, colorSet});
                                            }}
                                    >
                                        {Object.keys(GeneratorColorSet).map(key =>
                                            <option key={`fur-gen-color-set-${key.toLowerCase()}`}
                                                    value={key}>
                                                {key.charAt(0) + key.substring(1).toLowerCase()}
                                            </option>
                                        )}
                                    </select>
                                    <span className="fur-color-set-note" hidden={query.colors <= 0}>
                                        {GeneratorColorSet.hasOwnProperty(query.colorSet!)
                                            ? `(${GeneratorColorSet[query.colorSet!]})`
                                            : ''}
                                    </span>
                                </div>
                            </div>
                        </form>
                        <div className="fur-form-buttons">
                            <input type="submit"
                                   className="form-button-primary"
                                   value={`${!state ? 'Generate!' : 'Reroll!'}`}
                                   onClick={() => submitForm()} />
                            <input type="button"
                                   className="form-button-secondary"
                                   value="Reset!"
                                   onClick={() => setQuery({..._base_state})}
                            />
                        </div>
                        {state === undefined ? <></> : <div className="fur-gen-result">
                            {state.creatureName
                                    ? <p className="center">
                                        Your result is<br/>
                                        {formatColors()} {state!.modifier || ''} <span>{getFullCreatureName()}</span>
                                    </p>
                                    : <p className="center">Something went wrong. I couldn't really think of anything based
                                        on the options you gave me.</p>
                            }
                        </div>}
                        <div>
                            <p className="center">
                                You can find the full species list by
                                clicking <NavLink to="list" target="_blank" rel="noreferrer noopener">here</NavLink>.
                            </p>
                        </div>
                    </div>
                    <div className="fur-gen-image">
                        <img src={_image_service.getCdnImageSrc('chase/assets/chase_thinking.png')}
                             alt="Hmmm..."
                             title="What I should do...?"/>
                    </div>
                </div>
            </ContentBox>
        </>
    );
}

export default FursonaGeneratorPage;